// A25 — Newsfeed + Public Findings. Three sources are surfaced
// distinctly so industry signal cannot masquerade as Waytide
// evidence: (1) workspace findings (private), (2) news_items
// (industry/release), (3) model_releases (queueable for shadow trial).

import { useEffect, useState } from "react";
import { TabScrollbox } from "../../chrome/TabScrollbox";
import {
  ApiError,
  listFindings,
  listModelReleases,
  listNewsItems,
  type FindingRow,
  type ModelReleaseRow,
  type NewsItemRow,
} from "../../api/client";

interface Props {
  workspaceId: number;
}

export function NewsfeedTab({ workspaceId }: Props) {
  const [findings, setFindings] = useState<FindingRow[]>([]);
  const [news, setNews] = useState<NewsItemRow[]>([]);
  const [releases, setReleases] = useState<ModelReleaseRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    setError(null);
    Promise.all([
      listFindings(workspaceId, { includeOrg: true, limit: 100 }),
      listNewsItems(),
      listModelReleases(),
    ])
      .then(([f, n, r]) => {
        if (cancelled) return;
        setFindings(f);
        setNews(n);
        setReleases(r);
      })
      .catch((e) =>
        setError(e instanceof ApiError ? e.message : String(e)),
      )
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  return (
    <TabScrollbox>
      <header className="mb-4">
        <p className="max-w-2xl text-xs text-muted">
          Findings are receipt-backed claims. Industry items are signals
          only — they cannot be marked as Waytide-authored evidence.
          Model releases queue low-budget shadow trials when an opt-in
          workspace has learning budget.
        </p>
      </header>
      {busy && <p className="text-xs text-muted">Loading…</p>}
      {error && <p className="text-xs text-danger">{error}</p>}

      <section className="mb-6 flex flex-col gap-2">
        <header className="border-b border-edge pb-1">
          <h3 className="text-xs font-semibold text-muted">Findings</h3>
        </header>
        {findings.length === 0 ? (
          <p className="text-[0.625rem] text-faint">
            No findings yet. They land when Shore writes them from
            receipt evidence.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {findings.map((f) => (
              <FindingCard key={f.finding_id} row={f} />
            ))}
          </ul>
        )}
      </section>

      <section className="mb-6 flex flex-col gap-2">
        <header className="border-b border-edge pb-1">
          <h3 className="text-xs font-semibold text-muted">Industry signal</h3>
        </header>
        {news.length === 0 ? (
          <p className="text-[0.625rem] text-faint">No news yet.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {news.slice(0, 30).map((n) => (
              <li
                key={n.news_id}
                className="rounded-md border border-edge bg-surface-raised px-3 py-2"
                data-testid={`newsitem-${n.news_id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-medium text-primary">
                    {n.headline}
                  </span>
                  <span className="font-code text-[0.625rem] text-faint">
                    {n.kind}
                  </span>
                </div>
                <p className="mt-1 text-[0.625rem] text-muted">{n.body_md}</p>
                {n.url && (
                  <a
                    href={n.url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-1 inline-block text-[0.625rem] text-accent hover:underline"
                  >
                    source →
                  </a>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <header className="border-b border-edge pb-1">
          <h3 className="text-xs font-semibold text-muted">Model releases</h3>
        </header>
        {releases.length === 0 ? (
          <p className="text-[0.625rem] text-faint">
            No model releases logged yet — model_discovery_pass polls
            OpenRouter every 6h.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {releases.slice(0, 40).map((r) => (
              <li
                key={r.release_id}
                className="rounded-md border border-edge bg-surface-raised px-3 py-2"
                data-testid={`release-${r.release_id}`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-xs font-medium text-primary">
                    {r.display_name}
                  </span>
                  <span className="font-code text-[0.625rem] text-faint">
                    {r.provider}
                  </span>
                </div>
                <p className="mt-0.5 font-code text-[0.625rem] text-muted">
                  {r.model_id}
                </p>
                <p className="mt-1 text-[0.625rem] text-muted">
                  {r.context_window > 0
                    ? `${(r.context_window / 1000).toFixed(0)}k ctx`
                    : "ctx ?"}
                  {r.queued_for_shadow && (
                    <span className="ml-2 text-warning">queued for shadow</span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </TabScrollbox>
  );
}

function FindingCard({ row }: { row: FindingRow }) {
  let tags: string[] = [];
  try {
    tags = JSON.parse(row.task_tags_json);
  } catch {
    /* ignore */
  }
  const tone =
    row.scope === "public_aggregate"
      ? "border-accent"
      : row.scope === "org"
        ? "border-warning/60"
        : "border-edge";
  return (
    <li
      className={`rounded-md border-l-2 ${tone} bg-surface-raised px-3 py-2`}
      data-testid={`finding-${row.finding_id}`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-primary">{row.claim}</span>
        <span className="font-code text-[0.625rem] text-faint">
          {row.scope}
        </span>
      </div>
      <p className="mt-1 text-[0.625rem] text-muted">
        {row.aggregate_count > 0 && (
          <span>n={row.aggregate_count} · </span>
        )}
        confidence {Math.round(row.confidence * 100)}%
        {tags.length > 0 && (
          <span className="ml-2">
            {tags.map((t) => (
              <span
                key={t}
                className="ml-1 rounded bg-surface px-1 py-0.5 font-code text-[0.625rem] text-faint"
              >
                {t}
              </span>
            ))}
          </span>
        )}
      </p>
      <details className="mt-2">
        <summary className="cursor-pointer text-[0.625rem] text-muted hover:text-primary">
          evidence_refs
        </summary>
        <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap font-code text-[0.625rem] text-faint">
          {row.evidence_refs_json}
        </pre>
      </details>
    </li>
  );
}
